import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { AutocompleteController } from '@components/controllers/AutocompleteController';
import { StepProps } from '@features/reports2/types';
import { getMeanWindowOptions } from '@features/reports2/utils/meanWindowOptions';
import { getAvailableTimezonesSorted, getBrowserTimezone } from '@features/reports2/utils/timezone';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { FC, Suspense } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CreateReportFieldValues } from '../../validation';
import { InnerPaper, StepContentBox, StepTitle } from '../common';
import DateTimeRangePicker from '@features/standardDesign/components/DateTimeRangePicker/index';

const SuspenseComponent: FC<StepProps> = (props) => {
  const methods = useFormContext<CreateReportFieldValues>();
  const { i18n, _ } = useLingui();

  const meanWindowOptions = getMeanWindowOptions(i18n);
  const allTimezones = getAvailableTimezonesSorted(getBrowserTimezone());

  const errors = methods.formState.errors.step2;
  const nextStepEnabled = !errors?.meanWindow && !errors?.period && !errors?.timezone;

  return (
    <StepContentBox
      actions={{
        ...props.actions,
        nextStepEnabled: props.actions.nextStepEnabled && nextStepEnabled,
        onNextStep: () => {
          methods.trigger('step2').then((success) => {
            if (success) {
              props.actions.onNextStep();
            }
          });
        },
      }}
    >
      <StepTitle
        title={<Trans>Periodo de tiempo</Trans>}
        description={<Trans>Selecciona y define los periodos de tiempo para tu reporte.</Trans>}
      />
      <InnerPaper>
        <Typography fontWeight="bold" mb={2}>
          <Trans>Periodo de tiempo</Trans>
        </Typography>
        <Box maxWidth={400}>
          <Controller
            control={methods.control}
            name="step2.period"
            render={({ field }) => (
              <DateTimeRangePicker value={field.value} onChange={field.onChange} fullWidth hideAbsoluteTime={false} />
            )}
          />
        </Box>
      </InnerPaper>
      <InnerPaper mt={4}>
        <Typography fontWeight="bold" mb={2}>
          <Trans>Promedio y zona horaria</Trans>
        </Typography>
        <Box display="flex" gap={4}>
          <Controller
            control={methods.control}
            name="step2.meanWindow"
            render={({ field, fieldState }) => (
              <Autocomplete
                value={field.value ?? 'raw'}
                onChange={(_, value) => field.onChange(value)}
                options={meanWindowOptions.map((window) => window.value)}
                fullWidth
                size="small"
                sx={{ maxWidth: 400 }}
                getOptionLabel={(value) => {
                  if (value === 'raw') value = null;
                  return (
                    meanWindowOptions.find((option) => option.value === value)?.label ?? value ?? ''
                  );
                }}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={_(msg`Promediar datos`)}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
          <AutocompleteController
            control={methods.control}
            name="step2.timezone"
            label={_(msg`Zona horaria`)}
            options={allTimezones}
            slotProps={{
              autocomplete: {
                disableClearable: true,
                fullWidth: true,
                size: 'small',
                sx: { maxWidth: 400 },
              },
            }}
          />
        </Box>
      </InnerPaper>
    </StepContentBox>
  );
};

export const TimePeriodStep: FC<StepProps> = (props) => {
  return (
    <ApolloErrorBoundary>
      <Suspense>
        <SuspenseComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
